import { useState } from "react";
import { toast } from "sonner";
import { Link as LinkIcon, FileText, Sparkles } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";

export const ImportPanel = ({ onExtracted }) => {
  const api = useApi();
  const [mode, setMode] = useState("paste");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      let job = await api("post", "/admin/extract", mode === "url" ? { url } : { text });
      for (let i = 0; job.state === "generating"; i++) {
        if (i > 80) throw new Error("Import is taking too long. Try a shorter paste.");
        await new Promise((r) => setTimeout(r, 4000));
        job = await api("get", `/admin/extract/${job.id}`);
      }
      if (job.state === "error") throw new Error(job.error);
      const m = job.meeting;
      toast.success(`Found ${m.races.length} races at ${m.track}. Check and save below.`);
      onExtracted(m);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const tab = (k, Icon, label) => (
    <button data-testid={`import-mode-${k}`} onClick={() => setMode(k)}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${mode === k ? "bg-blue-600" : "text-slate-400 hover:text-white"}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );

  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5">
      <h2 className="font-display text-lg font-bold mb-1">Import a form guide</h2>
      <p className="text-sm text-slate-400 mb-4">Paste the text of any form guide, or a page link. The AI reads it into races and runners for you to check.</p>
      <div className="inline-flex bg-[#0B101D] rounded-full p-1 mb-4">{tab("paste", FileText, "Paste text")}{tab("url", LinkIcon, "Page link")}</div>
      {mode === "paste" ? (
        <textarea data-testid="admin-paste-input" rows={10} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Paste the whole meeting: track, date, races, runners, barriers, weights, jockeys, trainers and last starts…"
          className="w-full rounded-xl bg-[#0B101D] border border-white/10 p-3 text-sm font-mono outline-none focus:border-blue-500" />
      ) : (
        <>
          <input data-testid="admin-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…"
            className="w-full rounded-full bg-[#0B101D] border border-white/10 px-4 py-2.5 text-sm outline-none focus:border-blue-500" />
          <p className="text-xs text-slate-500 mt-2">Some racing sites load form with scripts or block readers. If nothing is found, copy the page text and paste it instead.</p>
        </>
      )}
      <button data-testid="admin-extract-button" onClick={run} disabled={busy || (mode === "paste" ? !text.trim() : !url.trim())}
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 font-semibold text-sm disabled:opacity-50 transition-colors">
        <Sparkles className={`w-4 h-4 ${busy ? "animate-pulse" : ""}`} />{busy ? "Reading form guide… (can take 1-3 minutes)" : "Extract with AI"}
      </button>
    </section>
  );
};
